import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileText,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  UploadCloud,
  ArrowUpRight,
  FilePlus2,
  AlarmClock,
  PenLine,
  Stamp,
  AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";

function Stat({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-900 text-white dark:bg-slate-700",
    emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-500 text-white",
    sky: "bg-sky-600 text-white",
  };
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-sm transition"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </div>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

function ActionTile({ icon: Icon, title, desc, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:border-emerald-500 hover:shadow-md transition group"
    >
      <div className="h-9 w-9 rounded-md bg-slate-900 dark:bg-slate-700 text-emerald-400 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-4 text-sm font-semibold">{title}</div>
      <div className="mt-1 text-xs text-slate-500">{desc}</div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [awaiting, setAwaiting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/analytics/summary"),
          api.get("/me/awaiting"),
        ]);
        setSummary(s.data);
        setAwaiting(a.data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const c = summary?.counts || {};
  const pending = (c.sent || 0) + (c.viewed || 0) + (c.partially_signed || 0);
  const expired = c.expired || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
            Command center
          </div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight">
            Welcome back, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Here's what's in flight across your organization.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/app/templates" className="flex-1 sm:flex-none">
            <Button variant="outline" className="gap-2 w-full sm:w-auto">
              <FilePlus2 className="h-4 w-4" /> Use template
            </Button>
          </Link>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-1 sm:flex-none"
            onClick={() => navigate("/app/documents/upload")}
            data-testid="dashboard-upload-cta"
          >
            <UploadCloud className="h-4 w-4" /> Upload document
          </Button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ActionTile
          icon={UploadCloud}
          title="Upload a document"
          desc="Drop in a PDF and prepare fields"
          onClick={() => navigate("/app/documents/upload")}
          testid="quick-upload"
        />
        <ActionTile
          icon={FilePlus2}
          title="Use a template"
          desc="Reuse fields and recipients"
          onClick={() => navigate("/app/templates")}
          testid="quick-template"
        />
        <ActionTile
          icon={Users}
          title="Invite a teammate"
          desc="Add a sender, approver, or HR"
          onClick={() => navigate("/app/team")}
          testid="quick-invite"
        />
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Stat icon={FileText} label="Total" value={summary?.total ?? 0} testid="stat-total" />
        <Stat icon={Clock} label="Pending" value={pending} tone="amber" testid="stat-pending" />
        <Stat icon={CheckCircle2} label="Completed" value={c.completed ?? 0} tone="emerald" testid="stat-completed" />
        <Stat icon={XCircle} label="Declined" value={c.declined ?? 0} tone="red" testid="stat-declined" />
        <Stat icon={AlertTriangle} label="Expired" value={expired} tone="sky" testid="stat-expired" />
      </div>

      {/* My queue */}
      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
          data-testid="queue-approvals"
        >
          <div className="flex items-center gap-2">
            <Stamp className="h-4 w-4 text-emerald-600" />
            <div className="text-sm font-semibold">Awaiting my approval</div>
          </div>
          <div className="mt-3 text-3xl font-bold tracking-tight">
            {awaiting?.approvals ?? 0}
          </div>
          <Link
            to="/app/documents"
            className="mt-2 text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1 hover:underline"
          >
            Review queue <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
          data-testid="queue-signatures"
        >
          <div className="flex items-center gap-2">
            <PenLine className="h-4 w-4 text-emerald-600" />
            <div className="text-sm font-semibold">Awaiting my signature</div>
          </div>
          <div className="mt-3 text-3xl font-bold tracking-tight">
            {awaiting?.signatures ?? 0}
          </div>
          <Link
            to="/app/documents"
            className="mt-2 text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1 hover:underline"
          >
            View documents <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
          data-testid="queue-expiries"
        >
          <div className="flex items-center gap-2">
            <AlarmClock className="h-4 w-4 text-amber-600" />
            <div className="text-sm font-semibold">Upcoming expiries</div>
          </div>
          {awaiting?.upcoming_expiries?.length ? (
            <div className="mt-3 space-y-2">
              {awaiting.upcoming_expiries.slice(0, 3).map((d) => (
                <Link
                  key={d.id}
                  to={`/app/documents/${d.id}`}
                  className="block text-sm hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-1.5 -mx-2 rounded"
                >
                  <div className="truncate font-medium">{d.title}</div>
                  <div className="text-[10px] font-mono text-slate-500">
                    {new Date(d.expires_at).toLocaleDateString()}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-500">No upcoming expiries</div>
          )}
        </div>
      </div>

      {/* Trend chart + completion rate */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold">Sent vs completed (14 days)</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Trend at a glance — full breakdown in Analytics.
              </p>
            </div>
            <Link
              to="/app/analytics"
              className="text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1"
            >
              Open <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="h-56 mt-4">
            <ResponsiveContainer>
              <AreaChart data={summary?.timeseries || []}>
                <defs>
                  <linearGradient id="sent" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#0f172a" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0f172a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="comp" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area dataKey="sent" stroke="#0f172a" fill="url(#sent)" strokeWidth={2} />
                <Area dataKey="completed" stroke="#059669" fill="url(#comp)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Completion rate
          </div>
          <div className="mt-1 text-4xl font-bold tracking-tight">
            {summary?.completion_rate ?? 0}%
          </div>
          <div className="mt-4 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${summary?.completion_rate ?? 0}%` }}
            />
          </div>
          <div className="mt-5 space-y-2">
            {["draft", "sent", "viewed", "partially_signed", "completed"].map((s) => {
              const n = c[s] || 0;
              const pct = summary?.total ? (n / summary.total) * 100 : 0;
              return (
                <div key={s}>
                  <div className="flex items-center justify-between text-[11px] mb-0.5">
                    <span className="capitalize text-slate-600 dark:text-slate-400">
                      {s.replace(/_/g, " ")}
                    </span>
                    <span className="font-mono text-slate-500">{n}</span>
                  </div>
                  <div className="h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div className="h-full bg-slate-800 dark:bg-slate-300" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent */}
      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold">Recent documents</h3>
            <p className="text-xs text-slate-500 mt-0.5">Most recent uploads.</p>
          </div>
          <Link to="/app/documents" className="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900">
            View all
          </Link>
        </div>
        <div>
          {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
          {!loading && (summary?.recents || []).length === 0 && (
            <div className="p-10 text-center">
              <FileText className="h-8 w-8 mx-auto text-slate-400" />
              <div className="mt-3 text-sm font-medium">No documents yet</div>
              <div className="text-xs text-slate-500 mt-1">Upload your first PDF.</div>
              <Button
                onClick={() => navigate("/app/documents/upload")}
                className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Upload document
              </Button>
            </div>
          )}
          {(summary?.recents || []).map((d) => (
            <Link
              key={d.id}
              to={`/app/documents/${d.id}`}
              data-testid={`recent-doc-${d.id}`}
              className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {d.recipients_count}
                    </span>
                    <span className="font-mono">
                      {new Date(d.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <StatusBadge status={d.status} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
